import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle,
  FileText,
  Loader2,
  RefreshCw,
  Settings,
  Wrench,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { explainRecruitmentItem } from '../../lib/recruitmentActionGuide';

const API = API_BASE;

const MIN_REASON = { reject: 10, recover: 3, repair: 3 };

/**
 * Guided admin wizard — employment pack, agreement repair, recover, re-upload requests.
 */
export default function FixItWizard({
  open,
  onOpenChange,
  employeeId,
  employeeName = '',
  blockers = [],
  onRefresh,
  onNavigateToCompliance,
  onRequestDocument,
  isAuditor = false,
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [packReadiness, setPackReadiness] = useState(null);
  const [repairPlan, setRepairPlan] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [reason, setReason] = useState('');
  const [agreementType, setAgreementType] = useState('contract_acceptance');
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const fetchPackReadiness = useCallback(async () => {
    if (!employeeId || !token) return;
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/employment-pack/readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackReadiness(res.data?.readiness || null);
    } catch {
      setPackReadiness(null);
    }
  }, [employeeId, token]);

  const runVerification = useCallback(async () => {
    if (!employeeId || !token) return null;
    setVerifyLoading(true);
    try {
      const [auditRes, packRes] = await Promise.all([
        axios.get(`${API}/admin/agreement-audit/${employeeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/employees/${employeeId}/employment-pack/readiness`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const divergences = auditRes.data?.divergence_summary?.divergences || [];
      const verdict = auditRes.data?.divergence_summary?.verdict || '';
      const packReady = Boolean(packRes.data?.readiness?.ready);
      const contractState = auditRes.data?.read_employee_agreement_state?.contract_acceptance?.status;
      const handbookState = auditRes.data?.read_employee_agreement_state?.handbook_acknowledgement?.status;
      const result = {
        allClear: divergences.length === 0 && packReady,
        syncOk: divergences.length === 0,
        packReady,
        verdict,
        divergences,
        contractState,
        handbookState,
        checkedAt: new Date().toISOString(),
      };
      setVerifyResult(result);
      return result;
    } catch {
      setVerifyResult({ allClear: false, error: true });
      return null;
    } finally {
      setVerifyLoading(false);
    }
  }, [employeeId, token]);

  useEffect(() => {
    if (open) {
      fetchPackReadiness();
      setActiveAction(null);
      setReason('');
      setRepairPlan(null);
      setVerifyResult(null);
    }
  }, [open, fetchPackReadiness]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const handlePreparePack = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/employment-pack/prepare`,
        { apply_org_defaults: true, render: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Contract and handbook PDFs prepared.');
      await fetchPackReadiness();
      onRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.readiness) {
        setPackReadiness(detail.readiness);
        toast.error(detail.message || 'Employment pack is not ready yet.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not prepare employment pack.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRepairDryRun = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/agreements/repair`,
        { employee_id: employeeId, apply: false },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRepairPlan(res.data?.result || null);
      const contractAction = res.data?.result?.contract?.action || 'no_change';
      const handbookAction = res.data?.result?.handbook?.action || 'no_change';
      if (contractAction === 'no_change' && handbookAction === 'no_change') {
        toast.info('Agreements are already in sync — no repair needed.');
      } else {
        setActiveAction('repair_apply');
        toast.success(`Repair plan ready: contract ${contractAction}, handbook ${handbookAction}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair check failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRepairApply = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/agreements/repair`,
        { employee_id: employeeId, apply: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Agreement repair applied.');
      setRepairPlan(null);
      setActiveAction(null);
      onRefresh?.();
      const verified = await runVerification();
      if (verified?.allClear) {
        toast.success('Verified — agreements are in sync and employment pack is ready.');
      } else if (verified?.syncOk) {
        toast.info('Agreements are in sync. Employment pack may still need preparation.');
      } else if (verified) {
        toast.warning('Repair applied but sync check still shows issues — see verification below.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Repair failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async () => {
    if (reason.trim().length < MIN_REASON.recover) {
      toast.error(`Reason must be at least ${MIN_REASON.recover} characters.`);
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/agreements/recover`,
        { agreement_type: agreementType, reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Agreement recovered — worker can act on the latest version.');
      setReason('');
      setActiveAction(null);
      onRefresh?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Recovery failed.');
    } finally {
      setLoading(false);
    }
  };

  const packBlockers = [
    ...(packReadiness?.contract?.blockers || []),
    ...(packReadiness?.handbook?.blockers || []),
  ];

  const blockerGuides = (Array.isArray(blockers) ? blockers : [])
    .filter((b) => typeof b === 'string' ? Boolean(b.trim()) : Boolean(b?.label || b?.message || b?.reason))
    .map(explainRecruitmentItem);

  const handleOpenChange = (next) => {
    onOpenChange?.(next);
  };

  if (isAuditor) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal>
      <DialogContent
        className="max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="fix-it-wizard"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Follow up on checks — {employeeName || 'Person file'}
          </DialogTitle>
          <DialogDescription>
            Read what needs doing, who should do it, and where to go. CareDock does not make hiring decisions or change an applicant's answers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Verification — how to know it worked */}
          <section className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Did the fix work?</h3>
              <Button size="sm" variant="outline" onClick={runVerification} disabled={verifyLoading || loading}>
                {verifyLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                Check now
              </Button>
            </div>
            <p className="text-xs text-slate-600 mb-2">
              After repair or prepare, click Check now. All green means admin and worker portals agree.
            </p>
            {verifyResult && !verifyResult.error && (
              <ul className="text-xs space-y-1.5">
                <li className={verifyResult.syncOk ? 'text-green-700 flex items-center gap-1.5' : 'text-red-700 flex items-center gap-1.5'}>
                  {verifyResult.syncOk ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                  Agreement sync: {verifyResult.syncOk ? 'Admin and worker agree' : `${verifyResult.divergences?.length || 1} mismatch(es) remain`}
                </li>
                <li className={verifyResult.packReady ? 'text-green-700 flex items-center gap-1.5' : 'text-amber-700 flex items-center gap-1.5'}>
                  {verifyResult.packReady ? <CheckCircle className="h-3.5 w-3.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                  Employment pack: {verifyResult.packReady ? 'Ready to prepare PDFs' : 'Still missing settings or fields'}
                </li>
                {verifyResult.contractState && (
                  <li className="text-slate-600">Contract: {String(verifyResult.contractState).replace(/_/g, ' ')}</li>
                )}
                {verifyResult.handbookState && (
                  <li className="text-slate-600">Handbook: {String(verifyResult.handbookState).replace(/_/g, ' ')}</li>
                )}
                {!verifyResult.syncOk && verifyResult.divergences?.length > 0 && (
                  <li className="text-amber-800 mt-1">
                    {verifyResult.divergences.slice(0, 2).map((d) => (
                      <span key={d.field || d.impact} className="block">
                        • {d.impact || d.field}
                      </span>
                    ))}
                  </li>
                )}
                {!verifyResult.syncOk
                  && verifyResult.contractState === 'fully_executed'
                  && String(verifyResult.handbookState || '').includes('verified') && (
                  <li className="text-green-700 mt-1">
                    Contract and handbook are complete — any remaining sync warning is display-only and safe to ignore after refresh.
                  </li>
                )}
              </ul>
            )}
            {verifyResult?.allClear && (
              <p className="mt-2 text-xs font-medium text-green-700">
                All clear — close this wizard and confirm the red &quot;Agreement sync issue&quot; badge is gone on the profile.
              </p>
            )}
            {verifyResult?.error && (
              <p className="text-xs text-red-700">Could not run verification. Refresh the page and try again.</p>
            )}
          </section>

          {/* Employment pack */}
          <section className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-800">Employment pack</h3>
              {packReadiness?.ready ? (
                <Badge className="bg-green-600">Ready</Badge>
              ) : (
                <Badge variant="destructive">Incomplete</Badge>
              )}
            </div>
            {packReadiness?.handbook?.render_stale && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <p className="font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Handbook PDF needs re-prepare
                </p>
                <p className="mt-1 text-amber-800">
                  The stored handbook was rendered before the latest editorial cleanup (iCube placeholder text).
                  Click Prepare below to generate a clean PDF. Worker verification is preserved.
                </p>
              </div>
            )}
            {packBlockers.length > 0 && (
              <ul className="text-xs text-amber-800 space-y-1 mb-3">
                {packBlockers.slice(0, 4).map((b) => (
                  <li key={b.field}>• {b.label}: {b.fix_hint}</li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={handlePreparePack} disabled={loading || !packReadiness?.ready}>
                {packReadiness?.handbook?.render_stale ? 'Re-prepare handbook PDF' : 'Prepare contract & handbook'}
              </Button>
              <a href="/portal/settings" className="inline-flex items-center gap-1 text-xs text-primary hover:underline self-center">
                <Settings className="h-3 w-3" />
                Organisation settings
              </a>
            </div>
          </section>

          {/* Agreement sync */}
          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Agreement sync &amp; repair</h3>
            <p className="text-xs text-slate-600 mb-3">
              Aligns admin and worker views. Re-renders unsigned contracts if stale. Never discards signed PDFs.
            </p>
            {repairPlan && activeAction === 'repair_apply' && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-900">
                Contract: {repairPlan.contract?.action || 'no_change'} · Handbook: {repairPlan.handbook?.action || 'no_change'}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={handleRepairDryRun} disabled={loading}>
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Check repair plan
              </Button>
              {activeAction === 'repair_apply' && (
                <Button size="sm" onClick={handleRepairApply} disabled={loading}>
                  Apply repair
                </Button>
              )}
            </div>
          </section>

          {/* Historical documents: never silently reassign a guessed file path. */}
          <section className="rounded-xl border border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Uploaded file not showing?</h3>
            <p className="text-xs text-slate-600">
              Your existing document record is preserved. Automatic link repair is disabled
              because a stored filename or attachment link does not prove who owns the file.
              Check the original evidence with an authorised reviewer before requesting any
              replacement upload. This does not change the worker’s compliance status.
            </p>
          </section>

          {/* Recover stuck agreement */}
          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">Recover stuck agreement</h3>
            {activeAction !== 'recover' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setAgreementType('contract_acceptance'); setActiveAction('recover'); }}>
                  Recover contract
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setAgreementType('handbook_acknowledgement'); setActiveAction('recover'); }}>
                  Recover handbook
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Label>Reason (min {MIN_REASON.recover} characters)</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Worker stuck on old template after admin update" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleRecover} disabled={loading}>Confirm recovery</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setActiveAction(null); setReason(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </section>

          {/* Outstanding blockers */}
          {blockerGuides.length > 0 && (
            <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4" />
                Checks to finish before final approval ({blockerGuides.length})
              </h3>
              <p className="mb-3 text-xs text-amber-900">
                Other available tasks can continue while someone else is completing a check.
              </p>
              <ul className="space-y-2 max-h-72 overflow-y-auto mb-3">
                {blockerGuides.slice(0, 12).map((guide, idx) => (
                  <li key={idx} className="rounded-md border border-amber-200 bg-white p-3 text-xs text-slate-800">
                    <strong className="block text-sm text-slate-900">{guide.title}</strong>
                    <span className="mt-1 block"><strong>Who:</strong> {guide.owner}</span>
                    <span className="mt-1 block"><strong>Next:</strong> {guide.step}</span>
                    {guide.note ? <span className="mt-1 block text-slate-500">{guide.note}</span> : null}
                  </li>
                ))}
              </ul>
              {blockerGuides.length > 12 ? <p className="mb-2 text-xs">More checks are available in the full checklist.</p> : null}
              <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onNavigateToCompliance?.(); }}>
                <FileText className="h-3.5 w-3.5 mr-1" />
                Open full checklist
              </Button>
            </section>
          )}

          {/* Request re-upload hint */}
          {onRequestDocument && (
            <section className="rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-1">Request corrected document</h3>
              <p className="text-xs text-slate-600 mb-2">
                From the compliance checklist, use Request on any row to ask the worker for a new upload with a clear message.
              </p>
              <Button size="sm" variant="outline" onClick={() => { onOpenChange(false); onNavigateToCompliance?.(); }}>
                Go to checklist to request
              </Button>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}